import Vue from 'vue'
import VueRouter, { RouteConfig } from 'vue-router'
import DefaultContainer from '@/container/DefaultContainer.vue'
import authModule from '@/store/auth'
import listModule from '@/store/list'
import companyModule from '@/store/company'
import Cookies from 'js-cookie'

import axios from 'axios'

Vue.use(VueRouter)

const ifNotAuthenticated = (to: any, from: any, next: any) => {
  const state = localStorage.getItem(process.env.VUE_APP_STATE_KEY!) as any
  if ((state && !JSON.parse(state).auth.token) || !state) {
    next()
    return
  }
  next('/')
}

const ifAuthenticated = (to: any, from: any, next: any) => {
  const state = localStorage.getItem(process.env.VUE_APP_STATE_KEY!) as any
  if (state && JSON.parse(state).auth.token) {
    axios.defaults.headers['Authorization'] = `Bearer ${
      JSON.parse(state).auth.token
    }`
    next()
    return
  }
  next('/login')
}

const routes: Array<RouteConfig> = [
  {
    path: '/',
    redirect: '/dashboard',
    name: 'Home',
    component: DefaultContainer,
    beforeEnter: ifAuthenticated,
    children: [
      {
        path: '/dashboard',
        name: 'Dashboard',
        component: () =>
          import(/* webpackChunkName: 'dashboard' */ '../views/dashboard.vue'),
        beforeEnter: ifAuthenticated
      },
      {
        path: '/whitelabels/:page?',
        name: 'Whitelabels',
        component: () =>
          import(
            /* webpackChunkName: 'whitelabels' */ '../views/whitelabel/list.vue'
          ),
        beforeEnter: ifAuthenticated
      },
      {
        path: '/whitelabel/:wid',
        name: 'WhitelabelDetail',
        component: () =>
          import(
            /* webpackChunkName: 'whitelabelDetail' */ '../views/whitelabel/detail.vue'
          ),
        beforeEnter: ifAuthenticated
      },
      {
        path: '/companies/:page?',
        name: 'Companies',
        component: () =>
          import(
            /* webpackChunkName: 'companies' */ '../views/company/list.vue'
          ),
        beforeEnter: ifAuthenticated
      },
      {
        path: '/company/:cid',
        name: 'CompanyDetail',
        component: () =>
          import(
            /* webpackChunkName: 'companyDetail' */ '../views/company/detail.vue'
          ),
        beforeEnter: ifAuthenticated
      },
      {
        path: '/venues/:page?',
        name: 'AllVenues',
        component: () =>
          import(
            /* webpackChunkName: 'venueDetail' */ '../views/venue/list.vue'
          ),
        beforeEnter: ifAuthenticated
      },
      {
        path: '/venue/:vid',
        name: 'VenueDetail',
        component: () =>
          import(
            /* webpackChunkName: 'venueDetail' */ '../views/venue/detail.vue'
          ),
        beforeEnter: ifAuthenticated
      },
      {
        path: '/deal/:did',
        name: 'DealDetail',
        component: () =>
          import(
            /* webpackChunkName: 'dealDetail' */ '../views/deal/detail.vue'
          ),
        beforeEnter: ifAuthenticated
      },
      {
        path: '/deals/:page?',
        name: 'AllDeals',
        component: () =>
          import(/* webpackChunkName: 'deals' */ '../views/deal/list.vue'),
        beforeEnter: ifAuthenticated
      },
      {
        path: '/card-list/:cid',
        name: 'CardListDetail',
        component: () =>
          import(
            /* webpackChunkName: 'cardlists' */ '../views/cardList/detail.vue'
          ),
        beforeEnter: ifAuthenticated
      },
      {
        path: '/card-lists/:page?',
        name: 'AllCardLists',
        component: () =>
          import(
            /* webpackChunkName: 'cardlists' */ '../views/cardList/list.vue'
          ),
        beforeEnter: ifAuthenticated
      },
      {
        path: '/beacons/:page?',
        name: 'AllBeacons',
        component: () =>
          import(/* webpackChunkName: 'beacons' */ '../views/beacon/list.vue'),
        beforeEnter: ifAuthenticated
      },
      {
        path: '/tags/:page?',
        name: 'AllTags',
        component: () =>
          import(/* webpackChunkName: 'tags' */ '../views/tag/list.vue'),
        beforeEnter: ifAuthenticated
      },
      {
        path: '/promotion-codes',
        name: 'AllPromotionCodes',
        component: () =>
          import(
            /* webpackChunkName: 'promotionCodes' */ '../views/promotion-code/list.vue'
          ),
        beforeEnter: ifAuthenticated
      },
      {
        path: '/users/:page?',
        name: 'AllUsers',
        component: () =>
          import(/* webpackChunkName: 'users' */ '../views/user/list.vue'),
        beforeEnter: ifAuthenticated
      },
      {
        path: '/redemptions',
        name: 'RedemptionReviews',
        component: () =>
          import(
            /* webpackChunkName: 'redemptions' */ '../views/redemption/list.vue'
          ),
        beforeEnter: ifAuthenticated
      },
      {
        path: '/newNotification',
        name: 'NewNotification',
        component: () =>
          import(
            /* webpackChunkName: 'notifications' */ '../views/notification/create.vue'
          ),
        beforeEnter: ifAuthenticated
      },
      {
        path: '/scanCode',
        name: 'ScanCode',
        component: () =>
          import(/* webpackChunkName: 'scanCode' */ '../views/scan/scan.vue'),
        beforeEnter: ifAuthenticated
      }
    ]
  },
  {
    path: '/login/:wid?',
    name: 'Login',
    component: () =>
      import(/* webpackChunkName: 'login' */ '../views/login.vue'),
    beforeEnter: ifNotAuthenticated
  }
]

const router = new VueRouter({
  mode: 'history',
  routes
})

router.beforeEach((to, from, next) => {
  /* delete filter values if you navigate from 'companies' to 'deals',
   * but not if you navigate from 'companies' to 'company/:id' (vice versa)
   * or within 'companies/:page' from 'companies/1' with page = 1 to 'companies/2' */
  if (
    !(
      to.name === from.name ||
      Object.keys(to.params).some(el => el.endsWith('id')) ||
      Object.keys(from.params).some(el => el.endsWith('id'))
    )
  ) {
    listModule.resetListValues(to)
  }
  next()
})

axios.defaults.baseURL = process.env.VUE_APP_BASE_URL
axios.defaults.headers['content-Type'] = 'application/json'
axios.interceptors.response.use(
  response => {
    return response
  },
  error => {
    if (error.response.status === 401) {
      authModule.logout()
    }
    return Promise.reject(error)
  }
)
axios.interceptors.request.use(
  config => {
    if (authModule.isPartner || authModule.isLightPartner) {
      let companyId = ''
      if (companyModule.company) {
        companyId = companyModule.company._id
      } else if (authModule.user!.company) {
        companyId = authModule.user!.company._id
      }
      if (config.params) {
        Object.assign(config.params, { companyId })
      } else {
        Object.assign(config, { params: { companyId } })
      }
    }
    return config
  },
  error => {
    return Promise.reject(error)
  }
)

export default router
